import ExportButton from '../../components/ExportButton';
import WarehouseNameTooltip from '../../components/WarehouseNameTooltip';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDownToLine, ArrowLeft, ArrowRight, Box, Check, ChevronDown, ChevronLeft, ChevronRight, FileText, Gauge, House, Hourglass, Package, Save, Search } from 'lucide-react';
import TableScroll from '../../components/TableScroll';
import { addContainerCartonSheet, groupSelectedRecords } from './containerReport';
import { downloadWorkbook } from '../../utils/excelExport';
import CompactBreadcrumb from '../../components/CompactBreadcrumb';
import { CustomerLogo } from '../../components/CustomerModal';
import { autoAllocateContainerGoods, previewContainerGoods, emptyGoodsSummary, getContainerGoods, saveContainerGoods, updateContainer } from './containerPlanningService';
import '../customers/customers.css';
import usePersistentFilters, { validateFilters } from '../../components/usePersistentFilters';

const PRIORITY_FILTERS = [
  ['', 'All'],
  ['High', 'High'],
  ['Medium', 'Med'],
  ['Low', 'Low'],
];

const REVIEW_PAGE_SIZE = 15;
const emptyGoodsFilters = { availableQuery: '', selectedQuery: '', priority: '' };

function formatCount(value) {
  return (Number(value) || 0).toLocaleString('en-US');
}

function formatDecimal(value, digits = 6) {
  return (Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
}

function formatWeight(value) {
  const number = Number(value) || 0;
  return Number.isInteger(number)
    ? number.toLocaleString('en-US')
    : number.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

function splitWarehouseName(name) {
  const match = String(name || '').match(/^(.*?)(?:\s*\(([^)]+)\))\s*$/);
  if (match) return { title: match[1].trim(), code: match[2] };
  return { title: name || '—', code: '' };
}

function typeTone(name) {
  const key = String(name || '').toLowerCase();
  if (key.includes('pallet')) return 'pallet';
  if (key.includes('carton') || key.includes('ctn')) return 'carton';
  if (key.includes('bale')) return 'wooden';
  if (key.includes('wood')) return 'wooden';
  return 'other';
}

function ReviewFilterMenu({ open, onToggle, onClose, icon, label, value, children, disabled = false }) {
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const [position, setPosition] = useState({});
  useEffect(() => {
    if (!open) return undefined;
    function handlePointer(event) {
      if (!ref.current?.contains(event.target) && !panelRef.current?.contains(event.target)) onClose();
    }
    function handleKey(event) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open, onClose]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    function placeMenu() {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      const rect = trigger.getBoundingClientRect();
      const gutter = 8;
      const gap = 6;
      const panelHeight = Math.min(panel.scrollHeight, window.innerHeight - gutter * 2);
      const below = window.innerHeight - rect.bottom - gap - gutter;
      const above = rect.top - gap - gutter;
      const placeBelow = panelHeight <= below || below >= above;
      const availableHeight = Math.max(80, placeBelow ? below : above);
      const width = Math.min(Math.max(rect.width, 220), window.innerWidth - gutter * 2);
      setPosition({
        position: 'fixed',
        zIndex: 1000,
        left: Math.max(gutter, Math.min(rect.left, window.innerWidth - width - gutter)),
        top: placeBelow ? rect.bottom + gap : Math.max(gutter, rect.top - Math.min(panelHeight, availableHeight) - gap),
        width,
        fontFamily: getComputedStyle(trigger).fontFamily,
        '--cn-status-list-height': `${Math.max(48, availableHeight - 18)}px`,
      });
    }
    placeMenu();
    window.addEventListener('resize', placeMenu);
    document.addEventListener('scroll', placeMenu, true);
    return () => {
      window.removeEventListener('resize', placeMenu);
      document.removeEventListener('scroll', placeMenu, true);
    };
  }, [open]);

  return <div className="wh-menu" ref={ref}>
    <button ref={triggerRef} className={`wh-menu-trigger${open ? ' open' : ''}`} type="button" disabled={disabled} aria-haspopup="listbox" aria-expanded={open} onClick={onToggle}>
      {icon}
      <span className="wh-menu-label">{label}</span>
      <span className="wh-menu-value">{value}</span>
      {!disabled && <ChevronDown size={14} strokeWidth={1.8} className="wh-menu-caret" />}
    </button>
    {open && createPortal(<div ref={panelRef} className="wh-page wh-menu-panel cn-review-status-menu" style={position} role="listbox">{children}</div>, document.body)}
  </div>;
}


function cartonBreakdown(groups) {
  const buckets = new Map();
  groups.forEach(item => {
    const label = item.packaging_type || 'units';
    const qty = Number(item.total_carton_qty) || Number(item.packaging_quantity) || 0;
    if (qty > 0) buckets.set(label, (buckets.get(label) || 0) + qty);
  });
  return [...buckets.entries()].map(([label, qty]) => `${formatCount(qty)} ${label.toLowerCase()}`).join(' • ');
}

function formatQty(value) {
  const number = Number(value) || 0;
  return Number.isInteger(number) ? formatCount(number) : formatDecimal(number, 1);
}

function formatSelectionCount(value) {
  const count = Number(value) || 0;
  return `${formatCount(count)} ${count === 1 ? 'unit' : 'units'}`;
}

function matchesQuery(row, query) {
  if (!query) return true;
  const haystack = [row.product_name, ...(row.products || []).map(product => product.product_name), row.sku, row.customer_name, row.marko].join(' ').toLowerCase();
  return haystack.includes(query);
}

function matchesPriority(row, priority) {
  if (!priority) return true;
  return String(row.priority_name || '').toLowerCase().startsWith(priority.toLowerCase());
}

function wouldExceed(summary, rows) {
  const rank = { high: 1, medium: 2, low: 3 };
  const ordered = [...rows].sort((left, right) => (
    (rank[String(left.priority_name).trim().toLowerCase()] || 4)
    - (rank[String(right.priority_name).trim().toLowerCase()] || 4)
  ));
  const accepted = [];
  let weight = Number(summary.selected_weight) || 0;
  let volume = Number(summary.selected_volume) || 0;
  let overflow = '';
  for (const row of ordered) {
    const addWeight = Number(row.gross_weight) || 0;
    const addVolume = Number(row.cbm) || 0;
    const weightOver = summary.max_payload > 0 && weight + addWeight - summary.max_payload > 0.0001;
    const volumeOver = summary.capacity > 0 && volume + addVolume - summary.capacity > 0.0001;
    if (weightOver || volumeOver) {
      overflow = weightOver && volumeOver
        ? 'This item is greater than the remaining container capacity.'
        : weightOver
          ? 'This item is greater than the remaining weight capacity.'
          : 'This item is greater than the remaining volume capacity.';
      continue;
    }
    accepted.push(row);
    weight += addWeight;
    volume += addVolume;
  }
  return { accepted, overflow };
}

function capacityToast(onToast, message) {
  onToast?.(message, 'error', 'Capacity limit reached');
}

export function DraftSteps({ step }) {
  const items = [
    [1, 'Container Setup'],
    [2, 'Suggested Goods'],
    [3, 'Carton Packing & VGM'],
  ];
  return <ol className="cn-steps" aria-label="Container draft progress">
    {items.map(([index, label]) => {
      const state = step > index ? 'done' : step === index ? 'active' : 'upcoming';
      return <li key={index} className={state}>
        <span aria-hidden="true">{state === 'done' ? <Check size={13} strokeWidth={2.6} /> : index}</span>
        <div>
          <small>STEP {index}</small>
          <strong>{label}</strong>
        </div>
      </li>;
    })}
  </ol>;
}

function GoodsTable({ rows, checked, onToggle, onToggleAll, empty, prefix }) {
  const tableRef = useRef(null);
  const [columnSizing, setColumnSizing] = useState(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  useLayoutEffect(() => {
    const viewport = tableRef.current?.parentElement;
    if (!viewport) return;
    const updateWidth = () => setViewportWidth(viewport.clientWidth);
    updateWidth();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateWidth) : null;
    observer?.observe(viewport);
    window.addEventListener('resize', updateWidth);
    return () => { observer?.disconnect(); window.removeEventListener('resize', updateWidth); };
  }, []);
  useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const measurement = table.cloneNode(true);
    measurement.querySelector('colgroup')?.remove();
    measurement.setAttribute('aria-hidden', 'true');
    Object.assign(measurement.style, {
      position: 'absolute', visibility: 'hidden', pointerEvents: 'none',
      width: 'max-content', minWidth: '0', tableLayout: 'auto',
    });
    measurement.querySelectorAll('thead th').forEach(header => {
      header.style.setProperty('width', 'auto', 'important');
      header.style.setProperty('white-space', 'nowrap', 'important');
    });
    table.parentElement.appendChild(measurement);
    const widths = Array.from(measurement.querySelectorAll('thead th'), header => Math.ceil(header.getBoundingClientRect().width));
    widths[2] = Math.max(widths[2], 44);
    // Measure product text independently: table cell constraints can otherwise
    // report a width smaller than their overflowing content.
    const textMeasure = document.createElement('span');
    Object.assign(textMeasure.style, { position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap' });
    measurement.parentElement.appendChild(textMeasure);
    const productCells = [table.querySelector('thead th:nth-child(5)'), ...table.querySelectorAll('tbody td.cn-goods-product')];
    productCells.forEach(cell => {
      if (!cell) return;
      const style = getComputedStyle(cell);
      textMeasure.style.font = style.font;
      textMeasure.style.letterSpacing = style.letterSpacing;
      textMeasure.textContent = cell.textContent;
      widths[4] = Math.max(widths[4], Math.ceil(textMeasure.getBoundingClientRect().width)
        + parseFloat(style.paddingLeft || 0) + parseFloat(style.paddingRight || 0) + 12);
    });
    textMeasure.remove();
    measurement.remove();
    if (widths.some(width => width > 0)) {
      setColumnSizing({ widths, minimum: widths.reduce((sum, width) => sum + width, 0) });
    }
  }, [rows]);
  const columns = ['TYPE', 'QTY', 'CRT QTY', 'Product', 'PRD QTY',
    'PRD GROSS WT (KG)', 'PRD NET WT (KG)', 'GROSS WT (KG)', 'NET WT (KG)', 'CBM', 'MARKO'];
  const numericHeaderLines = {
    'PRD GROSS WT (KG)': ['PRD GROSS', 'WT (KG)'],
    'PRD NET WT (KG)': ['PRD NET', 'WT (KG)'],
    'GROSS WT (KG)': ['GROSS WT', '(KG)'],
    'NET WT (KG)': ['NET WT', '(KG)'],
  };
  const allChecked = rows.length > 0 && rows.every(row => checked.has(row.inventory_batch_product_id));
  const columnWidth = index => {
    const extra = Math.max(0, viewportWidth - columnSizing.minimum);
    const numericColumns = [2, 3, 5, 6, 7, 8, 9, 10];
    return columnSizing.widths[index] + (index === 4 ? extra / 2
      : numericColumns.includes(index) ? extra / (2 * numericColumns.length) : 0);
  };
  return <div className="cn-goods-tables"><TableScroll>
    <table ref={tableRef} className="wh-table cn-goods-table cn-goods-detail-table"
      style={columnSizing ? { minWidth: columnSizing.minimum } : undefined}>
      {columnSizing && <colgroup>{columnSizing.widths.map((width, index) => <col key={index} style={{
        width: columnWidth(index),
      }} />)}</colgroup>}
      <thead><tr>
        <th className="cn-check" style={columnSizing ? { width: columnSizing.widths[0] } : undefined}><input type="checkbox" checked={allChecked} disabled={!rows.length}
          aria-label={`${prefix} select all goods`} onChange={() => onToggleAll(rows)} /></th>
        {columns.map((column, index) => <th key={column} style={columnSizing ? {
          width: columnWidth(index + 1),
        } : undefined}>{numericHeaderLines[column]
          ? <>{numericHeaderLines[column][0]} <br />{numericHeaderLines[column][1]}</>
          : column}</th>)}
      </tr></thead>
      <tbody>
        {rows.flatMap(row => {
          const id = row.inventory_batch_product_id;
          const products = row.products?.length ? row.products : [row];
          const batches = new Map();
          products.forEach(product => {
            const key = product.inventory_batch_id ?? row.inventory_batch_id ?? id;
            if (!batches.has(key)) batches.set(key, []);
            batches.get(key).push(product);
          });
          return [...batches.values()].flatMap(batch => batch.map((product, index) => ({ product, batch, index })))
            .map(({ product, batch, index }, productIndex) => <tr key={`${row.selection_id || id}-${product.inventory_batch_product_id || productIndex}`}>
              {productIndex === 0 && <>
                <td className="cn-check" rowSpan={products.length}><input type="checkbox" checked={checked.has(id)}
                  aria-label={`${prefix} ${row.product_name || row.batch_number || 'goods'}`} onChange={() => onToggle(id)} /></td>
                <td rowSpan={products.length}><span className={`inv-type ${typeTone(row.packaging_type)}`}>{row.packaging_type || (row.selection_mode === 'PALLET' ? 'Pallet' : row.selection_mode === 'PRODUCT' ? 'Product' : 'Carton')}</span></td>
                <td rowSpan={products.length}>{formatQty(row.selection_mode === 'PALLET' ? row.quantity : row.packaging_quantity || row.quantity)}</td>
              </>}
              {index === 0 && <td rowSpan={batch.length}>{formatQty(product.batch_quantity ?? row.total_carton_qty ?? row.quantity)}</td>}
              <td className="cn-goods-product">{product.product_name || '?'}</td>
              <td>{formatQty(product.quantity)}</td>
              <td>{formatWeight(product.product_gross_weight ?? product.gross_weight)}</td>
              <td>{formatWeight(product.product_net_weight ?? product.net_weight)}</td>
              {productIndex === 0 && <>
                <td rowSpan={products.length}>{formatWeight(row.gross_weight)}</td>
                <td rowSpan={products.length}>{formatWeight(row.net_weight)}</td>
                <td rowSpan={products.length}>{formatDecimal(row.cbm)}</td>
                <td rowSpan={products.length}>{row.marko ? <span className="inv-marko">{row.marko}</span> : '?'}</td>
              </>}
            </tr>);
        })}
        {!rows.length && <tr><td className="wh-empty" colSpan={columns.length + 1}>{empty}</td></tr>}
      </tbody>
    </table>
  </TableScroll></div>;
}

export function ContainerDraftCartons({ draft, lookups = {}, onBack, onClose, onSetup, mode, onToast, readOnly = false }) {
  const [selected, setSelected] = useState([]);
  const [summary, setSummary] = useState(emptyGoodsSummary);
  const [container, setContainer] = useState(draft);
  const [page, setPage] = useState(1);
  const [openMenu, setOpenMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [exporting, setExporting] = useState(false);
  const toastRef = useRef(onToast);
  toastRef.current = onToast;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await getContainerGoods(draft.id);
        if (cancelled) return;
        setSelected(data.selected || []);
        setSummary({ ...emptyGoodsSummary, ...data.summary });
        if (data.container) setContainer(data.container);
      } catch (error) {
        if (!cancelled) toastRef.current?.(error.message, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [draft.id]);

  const records = useMemo(() => groupSelectedRecords(selected), [selected]);
  const visible = records;

  const pageCount = Math.max(1, Math.ceil(visible.length / REVIEW_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const start = visible.length ? (currentPage - 1) * REVIEW_PAGE_SIZE + 1 : 0;
  const end = Math.min(currentPage * REVIEW_PAGE_SIZE, visible.length);
  const pageRows = visible.slice((currentPage - 1) * REVIEW_PAGE_SIZE, currentPage * REVIEW_PAGE_SIZE);
  const reviewTableRef = useRef(null);
  const [reviewSizing, setReviewSizing] = useState({ widths: [152, 180, 80], digits: [72, 48, 100, 72, 124, 112, 112, 104, 104, 124, 124, 104], markoWidth: 54, minimum: 1540 });
  useLayoutEffect(() => {
    const table = reviewTableRef.current;
    const wrapper = table?.parentElement;
    if (!table || !wrapper) return;
    const probe = document.createElement('span');
    Object.assign(probe.style, { position: 'fixed', visibility: 'hidden', whiteSpace: 'nowrap' });
    document.body.appendChild(probe);
    const base = [88, 88, 80];
    ['thead th:nth-child(14), .inv-warehouse strong, .inv-warehouse small', 'thead th:nth-child(15), .inv-customer .wh-name', 'thead th:nth-child(4), .cn-review-product'].forEach((selector, index) => {
      table.querySelectorAll(selector).forEach(cell => {
        const style = getComputedStyle(cell);
        Object.assign(probe.style, { fontFamily: style.fontFamily, fontSize: style.fontSize, fontWeight: style.fontWeight, letterSpacing: style.letterSpacing, textTransform: style.textTransform });
        probe.textContent = cell.textContent;
        const customer = cell.closest('.inv-customer');
        const logo = customer?.querySelector('.cu-logo');
        const extra = index === 1 && customer ? (logo?.getBoundingClientRect().width || 30) + (parseFloat(getComputedStyle(customer).gap) || 8) : 0;
        base[index] = Math.max(base[index], Math.ceil(probe.getBoundingClientRect().width || cell.textContent.length * 6) + 16 + extra);
      });
    });
    const digits = [3, 4, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(column => {
      let width = 0;
      table.querySelectorAll(`thead th:nth-child(${column}), td[data-review-column="${column}"]`).forEach(cell => {
        const content = cell.firstElementChild || cell;
        const style = getComputedStyle(content);
        Object.assign(probe.style, { fontFamily: style.fontFamily, fontSize: style.fontSize, fontWeight: style.fontWeight, letterSpacing: style.letterSpacing, textTransform: style.textTransform });
        probe.textContent = cell.textContent;
        width = Math.max(width, Math.ceil(probe.getBoundingClientRect().width || cell.textContent.length * 6) + 18);
      });
      return width;
    });
    let markoWidth = 44;
    table.querySelectorAll('.cn-review-marko').forEach(cell => {
      const content = cell.firstElementChild || cell;
      const style = getComputedStyle(content);
      Object.assign(probe.style, { fontFamily: style.fontFamily, fontSize: style.fontSize, fontWeight: style.fontWeight, letterSpacing: style.letterSpacing, textTransform: style.textTransform });
      probe.textContent = cell.textContent;
      const padding = content === cell ? 0 : (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0)
        + (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
      const cellStyle = getComputedStyle(cell);
      const cellPadding = (parseFloat(cellStyle.paddingLeft) || 0) + (parseFloat(cellStyle.paddingRight) || 0);
      markoWidth = Math.max(markoWidth, Math.ceil(probe.getBoundingClientRect().width || cell.textContent.length * 6) + padding + cellPadding + 2);
    });
    probe.remove();
    const minimum = 64 + markoWidth + digits.reduce((sum, width) => sum + width, 0) + base.reduce((sum, width) => sum + width, 0);
    const resize = () => {
      const extra = Math.max(0, wrapper.clientWidth - minimum) / 3;
      const widths = base.map(width => width + extra);
      setReviewSizing(previous => previous.minimum === minimum && previous.markoWidth === markoWidth && previous.digits.every((width, index) => width === digits[index]) && previous.widths.every((width, index) => width === widths[index]) ? previous : { widths, digits, markoWidth, minimum });
    };
    resize();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize);
    observer?.observe(wrapper);
    window.addEventListener('resize', resize);
    return () => { observer?.disconnect(); window.removeEventListener('resize', resize); };
  }, [selected, currentPage]);

  useLayoutEffect(() => {
    const wrapper = reviewTableRef.current?.parentElement;
    if (!wrapper) return undefined;
    const resetScroll = () => { wrapper.scrollLeft = 0; };
    resetScroll();
    const frame = window.requestAnimationFrame(resetScroll);
    let settledFrame;
    const settlingFrame = window.requestAnimationFrame(() => { settledFrame = window.requestAnimationFrame(resetScroll); });
    return () => {
      window.cancelAnimationFrame(frame);
      window.cancelAnimationFrame(settlingFrame);
      window.cancelAnimationFrame(settledFrame);
    };
  }, [selected, currentPage, reviewSizing.minimum]);

  const warehouseMeta = splitWarehouseName(container?.warehouse_name || draft.warehouse_name);
  const containerStatuses = lookups.statuses || [];
  const statusLabel = (() => {
    const match = containerStatuses.find(row => row.key === (container?.status || draft.status));
    if (!match) return container?.status_label || 'Draft / Planning';
    return match.label;
  })();
  const cartonTotal = records.reduce((sum, item) => sum + (Number(item.total_carton_qty) || Number(item.packaging_quantity) || 0), 0)
    || summary.selected_quantity;
  const cartonNote = cartonBreakdown(records);

  function closeMenus() {
    setOpenMenu(null);
  }


  async function changeStatus(status) {
    if (readOnly || !container?.id || savingStatus || status === container.status) {
      closeMenus();
      return;
    }
    setSavingStatus(true);
    try {
      const updated = await updateContainer(container.id, {
        name: container.name || container.internal_reference,
        draft_date: container.draft_date,
        warehouse_id: container.warehouse_id,
        container_profile_id: container.profile_id,
        unit_of_measurement_id: container.unit_id,
        capacity: container.capacity,
        column_capacity: container.column_capacity || container.capacity,
        max_gross_payload: container.max_gross_payload,
        status,
      });
      setContainer(updated);
    } catch (error) {
      onToast?.(error.status === 403 || error.status === 404 ? "You don't have permission to edit this container." : error.message, 'error');
    } finally {
      setSavingStatus(false);
      closeMenus();
    }
  }

  return <section className="wh-page inv-page cn-page cn-review">
    <div className="cn-review-head">
      <div>
        <CompactBreadcrumb current="Selected Goods Review" items={readOnly
          ? [{ label: 'Containers', onNavigate: onClose || onBack }]
          : [
            { label: 'Containers', onNavigate: onClose },
            { label: mode === 'edit' ? 'Edit Container Draft' : 'Create Container Draft', onNavigate: onSetup },
            { label: 'Goods Selection', onNavigate: onBack },
          ]} />
        <h1>Selected Goods Review</h1>
      </div>
      <div className="cn-review-meta">
        <ExportButton disabled={loading || exporting} onExport={async format => {
          setExporting(true);
          try {
            const { default: ExcelJS } = await import('exceljs');
            const workbook = new ExcelJS.Workbook();
            addContainerCartonSheet(workbook, [{ container, selected }]);
            await downloadWorkbook(workbook, `container-${draft.id}-cargo-manifest.${format}`);
          } catch (error) { toastRef.current?.(error.message || 'Unable to export selected goods.', 'error'); }
          finally { setExporting(false); }
        }}>Export</ExportButton>
        <p className="cn-review-warehouse">
          <House size={14} strokeWidth={1.8} />
          <span>Warehouse:</span>
          <strong>{warehouseMeta.title}{warehouseMeta.code ? ` ${warehouseMeta.code}` : ''}</strong>
        </p>
        <ReviewFilterMenu
          open={openMenu === 'container-status'}
          onToggle={() => setOpenMenu(openMenu === 'container-status' ? null : 'container-status')}
          onClose={closeMenus}
          icon={<span className={`cn-status-dot ${container?.status_tone || 'draft'}`} />}
          label="Container Status"
          disabled={readOnly}
          value={savingStatus ? 'Saving...' : statusLabel}
        >
          <div className="wh-menu-list">
            {containerStatuses.map(row => {
              const selectedStatus = row.key === (container?.status || draft.status);
              return <button key={row.key} className={`wh-menu-item${selectedStatus ? ' selected' : ''}`} type="button" role="option" aria-selected={selectedStatus} disabled={readOnly || savingStatus} onClick={() => changeStatus(row.key)}>
                <span>{row.label}</span>
                {selectedStatus && <Check size={14} strokeWidth={2} />}
              </button>;
            })}
          </div>
        </ReviewFilterMenu>
      </div>
    </div>

    <div className="cn-review-kpis">
      <article className="wh-kpi cn-review-kpi">
        <span className="wh-kpi-icon blue"><FileText size={16} strokeWidth={1.8} /></span>
        <div className="wh-kpi-copy">
          <span>Total Cartons</span>
          <strong>{formatCount(cartonTotal)}</strong>
          {cartonNote ? <small>{cartonNote}</small> : null}
        </div>
      </article>
      <article className="wh-kpi cn-review-kpi">
        <span className="wh-kpi-icon blue"><Hourglass size={16} strokeWidth={1.8} /></span>
        <div className="wh-kpi-copy">
          <span>Total Weight</span>
          <strong>{formatCount(summary.selected_weight)} / {formatCount(summary.max_payload)} kg</strong>
          <b className="cn-review-bar" aria-hidden="true"><i style={{ width: `${Math.min(100, summary.payload_percent)}%` }} /></b>
        </div>
      </article>
      <article className="wh-kpi cn-review-kpi">
        <span className="wh-kpi-icon blue"><Box size={16} strokeWidth={1.8} /></span>
        <div className="wh-kpi-copy">
          <span>Volume</span>
          <strong>{formatDecimal(summary.selected_volume)} / {formatDecimal(summary.capacity)} m³</strong>
          <b className="cn-review-bar" aria-hidden="true"><i style={{ width: `${Math.min(100, summary.volume_percent)}%` }} /></b>
        </div>
      </article>
      <article className="wh-kpi cn-review-kpi">
        <span className="wh-kpi-icon green"><ArrowDownToLine size={16} strokeWidth={1.8} /></span>
        <div className="wh-kpi-copy">
          <span>Remaining Volume</span>
          <strong>{formatDecimal(summary.remaining_volume)} m³</strong>
          <small className="cn-review-ok">{formatDecimal(summary.free_volume_percent, 1)}% headroom buffer</small>
        </div>
      </article>
      <article className="wh-kpi cn-review-kpi">
        <span className="wh-kpi-icon green"><Gauge size={16} strokeWidth={1.8} /></span>
        <div className="wh-kpi-copy">
          <span>Remaining Payload</span>
          <strong>{formatCount(summary.remaining_payload)} kg</strong>
          <small className="cn-review-ok">{formatDecimal(summary.free_payload_percent, 1)}% safe capacity</small>
        </div>
      </article>
    </div>

    <div className="wh-panel">
      <TableScroll key={`container-review-${reviewSizing.minimum}`}>
        <table ref={reviewTableRef} className="wh-table inv-table cn-review-table" style={{ minWidth: reviewSizing.minimum, '--cn-review-marko-width': `${reviewSizing.markoWidth}px` }}>
          <colgroup>
            <col style={{ width: 64 }} />
            <col style={{ width: reviewSizing.digits[0] }} />
            <col style={{ width: reviewSizing.digits[1] }} />
            <col style={{ width: reviewSizing.widths[2] }} />
            <col style={{ width: reviewSizing.digits[2] }} />
            <col style={{ width: reviewSizing.digits[3] }} />
            <col style={{ width: reviewSizing.digits[4] }} />
            <col style={{ width: reviewSizing.digits[5] }} />
            <col style={{ width: reviewSizing.digits[6] }} />
            <col style={{ width: reviewSizing.digits[7] }} />
            <col style={{ width: reviewSizing.digits[8] }} />
            <col style={{ width: reviewSizing.digits[9] }} />
            <col style={{ width: reviewSizing.digits[10] }} />
            <col style={{ width: reviewSizing.widths[0] }} />
            <col style={{ width: reviewSizing.widths[1] }} />
            <col style={{ width: reviewSizing.markoWidth }} />
          </colgroup>
          <thead>
            <tr>
              <th>Type</th>
              <th>Qty</th>
              <th>Carton Qty</th>
              <th>Product</th>
              <th>Prod Qty</th>
              <th>Prod Gross</th>
              <th>Prod Net</th>
              <th>Carton Gross</th><th>Carton Net</th><th>Carton CBM</th>
              <th>Gross Wt</th>
              <th>Net Wt</th><th>Total CBM</th>
              <th>Warehouse</th>
              <th>Customer</th>
              <th className="cn-review-marko">Marko</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(item => {
              const products = item.products.length ? item.products : [{ id: `${item.id}-empty`, name: '—', quantity: '', gross_weight: '', net_weight: '' }];
              const warehouse = splitWarehouseName(item.warehouse_code || item.warehouse_name);
              return products.map((product, index) => (
                <tr key={`${item.id}-${product.id || index}`}>
                  {index === 0 && <td rowSpan={products.length}><span className={`inv-type ${typeTone(item.packaging_type)}`}>{item.packaging_type || '—'}</span></td>}
                  {index === 0 && <td data-review-column="3" rowSpan={products.length}>{item.packaging_quantity || '—'}</td>}
                  {product.cartonSpan > 0 && <td data-review-column="4" rowSpan={product.cartonSpan}>{formatWeight(product.carton_quantity)}</td>}
                  <td className="cn-review-product">{product.name || '—'}</td>
                  <td data-review-column="6">{product.quantity === '' ? '—' : formatWeight(product.quantity)}</td>
                  <td data-review-column="7">{product.gross_weight === '' ? '—' : formatWeight(product.gross_weight)}</td>
                  <td data-review-column="8">{product.net_weight === '' ? '—' : formatWeight(product.net_weight)}</td>
                  {product.cartonSpan > 0 && ['gross_weight', 'net_weight', 'cbm'].map((field, column) => <td key={field} data-review-column={9 + column} rowSpan={product.cartonSpan}>{formatDecimal(product[`carton_${field}`])}</td>)}
                  {index === 0 && <td data-review-column="12" rowSpan={products.length}>{formatWeight(item.gross_weight)}</td>}
                  {index === 0 && <td data-review-column="13" rowSpan={products.length}>{formatWeight(item.net_weight)}</td>}
                  {index === 0 && <td data-review-column="14" rowSpan={products.length}>{formatDecimal(item.cbm)}</td>}
                  {index === 0 && <td rowSpan={products.length}>
                    <WarehouseNameTooltip name={item.warehouse_name}><div className="inv-warehouse">
                      <strong>{warehouse.title}</strong>
                      {warehouse.code ? <small>{warehouse.code}</small> : item.warehouse_city ? <small>{item.warehouse_city}</small> : null}
                    </div></WarehouseNameTooltip>
                  </td>}
                  {index === 0 && <td rowSpan={products.length}>
                    <div className="inv-customer">
                      <CustomerLogo name={item.customer_name} logo={item.customer_logo} />
                      <span className="wh-name">{item.customer_name || '—'}</span>
                    </div>
                  </td>}
                  {index === 0 && <td className="cn-review-marko" rowSpan={products.length}>{item.marko ? <span className="inv-marko">{item.marko}</span> : '—'}</td>}
                </tr>
              ));
            })}
            {!pageRows.length && <tr><td className="wh-empty" colSpan={16}>{loading ? 'Loading selected goods...' : 'No selected goods.'}</td></tr>}
          </tbody>
        </table>
      </TableScroll>
      <div className="wh-pager">
        <p>Showing {start}–{end} of {visible.length} Selected Records</p>
        <div>
          <button type="button" disabled={currentPage === 1} onClick={() => { closeMenus(); setPage(currentPage - 1); }}><ChevronLeft size={14} />Previous</button>
          {Array.from({ length: pageCount }, (_, index) => <button key={index + 1} type="button" className={`wh-page-number${currentPage === index + 1 ? ' current' : ''}`} aria-current={currentPage === index + 1 ? 'page' : undefined} onClick={() => { closeMenus(); setPage(index + 1); }}>{index + 1}</button>)}
          <button type="button" disabled={currentPage === pageCount} onClick={() => { closeMenus(); setPage(currentPage + 1); }}>Next<ChevronRight size={14} /></button>
        </div>
      </div>
    </div>
  </section>;
}

export default function ContainerDraftGoods({ draft, onBack, onClose, mode, onContinue, onToast }) {
  const [available, setAvailable] = useState([]);
  const [selected, setSelected] = useState([]);
  const [summary, setSummary] = useState(emptyGoodsSummary);
  const [filters, setFilters, reconcileFilters] = usePersistentFilters('containerGoodsFilters', emptyGoodsFilters);
  const { availableQuery, selectedQuery, priority } = filters;
  const setAvailableQuery = value => setFilters(current => ({ ...current, availableQuery: value }));
  const setSelectedQuery = value => setFilters(current => ({ ...current, selectedQuery: value }));
  const setPriority = value => setFilters(current => ({ ...current, priority: typeof value === 'function' ? value(current.priority) : value }));
  const [checkedAvailable, setCheckedAvailable] = useState(() => new Set());
  const [checkedSelected, setCheckedSelected] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);
  const toastRef = useRef(onToast);
  toastRef.current = onToast;

  const applyPayload = useCallback(data => {
    setAvailable(data.available || []);
    setSelected(data.selected || []);
    setSummary({ ...emptyGoodsSummary, ...data.summary });
    reconcileFilters(values => validateFilters(values, emptyGoodsFilters, { priority: PRIORITY_FILTERS.map(([key]) => key) }));
  }, [reconcileFilters]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [data, suggested] = await Promise.all([
          getContainerGoods(draft.id),
          autoAllocateContainerGoods(draft.id),
        ]);
        if (!cancelled) {
          applyPayload(data);
          const availableIds = new Set((data.available || []).map(row => row.inventory_batch_product_id));
          setCheckedAvailable(new Set((suggested.selected || [])
            .map(row => row.inventory_batch_product_id).filter(id => availableIds.has(id))));
        }
      } catch (error) {
        if (!cancelled) toastRef.current(error.message, 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [draft.id, applyPayload]);

  const visibleAvailable = useMemo(
    () => available.filter(row => matchesQuery(row, availableQuery.trim().toLowerCase()) && matchesPriority(row, priority)),
    [available, availableQuery, priority],
  );
  const visibleSelected = useMemo(
    () => selected.filter(row => matchesQuery(row, selectedQuery.trim().toLowerCase())),
    [selected, selectedQuery],
  );

  function toggleSet(setter, id) {
    setter(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(setter, rows) {
    setter(current => {
      const ids = rows.map(row => row.inventory_batch_product_id);
      const allOn = ids.length && ids.every(id => current.has(id));
      const next = new Set(current);
      ids.forEach(id => allOn ? next.delete(id) : next.add(id));
      return next;
    });
  }

  async function moveRight() {
    const rows = available.filter(row => checkedAvailable.has(row.inventory_batch_product_id));
    if (!rows.length || busy) return;
    const { accepted, overflow } = wouldExceed(summary, rows);
    if (overflow) capacityToast(onToast, overflow);
    if (!accepted.length) return;
    setBusy('add');
    try {
      const data = await previewContainerGoods(draft.id, [...selected, ...accepted]);
      applyPayload(data);
      setCheckedAvailable(new Set());
    } catch (error) {
      const message = error.message || '';
      if (message.includes('is greater than the remaining')) capacityToast(onToast, message);
      else onToast(message, 'error');
    } finally {
      setBusy('');
    }
  }

  async function moveLeft() {
    const rows = selected.filter(row => checkedSelected.has(row.inventory_batch_product_id));
    if (!rows.length || busy) return;
    setBusy('remove');
    try {
      const data = await previewContainerGoods(draft.id, selected.filter(row => !checkedSelected.has(row.inventory_batch_product_id)));
      applyPayload(data);
      setCheckedSelected(new Set());
    } catch (error) {
      onToast(error.status === 403 || error.status === 404 ? "You don't have permission to edit this container." : error.message, 'error');
    } finally {
      setBusy('');
    }
  }

  async function handleSave(continueToCartons = false) {
    if (busy) return;
    setBusy('save');
    try {
      const data = await saveContainerGoods(draft.id, selected);
      applyPayload(data);
      onToast('Draft inventory saved.');
      if (continueToCartons) onContinue();
    } catch (error) {
      onToast(error.status === 403 || error.status === 404 ? "You don't have permission to edit this container." : error.message, 'error');
    } finally {
      setBusy('');
    }
  }

  async function handleCancel() {
    if (busy) return;
    setBusy('cancel');
    try {
      const saved = await getContainerGoods(draft.id);
      applyPayload(saved);
      setCheckedAvailable(new Set());
      setCheckedSelected(new Set());
    } catch (error) {
      onToast(error.message, 'error');
    } finally {
      setBusy('');
    }
  }

  function handleContinue() {
    if (!selected.length) {
      onToast('Select at least one inventory item before continuing.', 'error');
      return;
    }
    handleSave(true);
  }

  return <section className="wh-page cn-page cn-goods">
    <div className="cn-goods-head">
      <CompactBreadcrumb current="Goods Selection" disabled={Boolean(busy)} items={[
        { label: 'Containers', onNavigate: onClose },
        { label: mode === 'edit' ? 'Edit Container Draft' : 'Create Container Draft', onNavigate: onBack },
      ]} />
      <DraftSteps step={2} />
    </div>
    <div className="cn-goods-kpis">
      <article className="cn-goods-kpi">
        <span className="cn-goods-kpi-icon violet"><Box size={18} strokeWidth={1.8} /></span>
        <div>
          <span>Selected Weight</span>
          <strong>{formatCount(summary.selected_weight)} / {formatCount(summary.max_payload)} kg</strong>
          <small>{formatDecimal(summary.payload_percent, 1)}% loaded</small>
          <b className="cn-bar"><i style={{ width: `${Math.min(100, summary.payload_percent)}%` }} /></b>
        </div>
      </article>
      <article className="cn-goods-kpi">
        <span className="cn-goods-kpi-icon violet"><Package size={18} strokeWidth={1.8} /></span>
        <div>
          <span>Selected Volume</span>
          <strong>{formatDecimal(summary.selected_volume)} / {formatDecimal(summary.capacity)} m³</strong>
          <small>{formatDecimal(summary.volume_percent, 1)}% filled</small>
          <b className="cn-bar light"><i style={{ width: `${Math.min(100, summary.volume_percent)}%` }} /></b>
        </div>
      </article>
      <article className="cn-goods-kpi">
        <span className="cn-goods-kpi-icon green"><Check size={18} strokeWidth={2.4} /></span>
        <div>
          <span>Remaining Payload</span>
          <strong>{formatCount(summary.remaining_payload)} kg</strong>
          <small>{formatDecimal(summary.free_payload_percent, 1)}% free</small>
          <b className="cn-bar green"><i style={{ width: `${Math.min(100, summary.free_payload_percent)}%` }} /></b>
        </div>
      </article>
      <article className="cn-goods-kpi">
        <span className="cn-goods-kpi-icon violet"><Box size={18} strokeWidth={1.8} /></span>
        <div>
          <span>Remaining Volume</span>
          <strong>{formatDecimal(summary.remaining_volume)} m³</strong>
          <small>{formatDecimal(summary.free_volume_percent, 1)}% free</small>
          <b className="cn-bar violet"><i style={{ width: `${Math.min(100, summary.free_volume_percent)}%` }} /></b>
        </div>
      </article>
    </div>

    <div className="cn-goods-board">
      <article className="cn-card cn-goods-panel">
        <header className="cn-goods-panel-head">
          <h2><span className="cn-goods-title-icon"><Box size={14} strokeWidth={1.7} /></span>Available Inventory</h2>
          <div>
            <button className={`cn-priority-link${priority === 'High' ? ' active' : ''}`} type="button" onClick={() => setPriority(current => (current === 'High' ? '' : 'High'))}>High Priority</button>
            <button className="cn-text-btn" type="button" onClick={() => setCheckedAvailable(new Set())}>Deselect</button>
          </div>
        </header>
        <div className="cn-goods-tools">
          <label className="wh-search">
            <Search size={13} />
            <input type="search" placeholder="Search available goods..." value={availableQuery} onChange={event => setAvailableQuery(event.target.value)} />
          </label>
          <div className="cn-pills" role="tablist" aria-label="Priority">
            {PRIORITY_FILTERS.map(([key, label]) => <button key={key || 'all'} className={`cn-pill${priority === key ? ' active' : ''}`} type="button" onClick={() => setPriority(key)}>{label}</button>)}
          </div>
        </div>
        <GoodsTable
          rows={visibleAvailable}
          checked={checkedAvailable}
          onToggle={id => toggleSet(setCheckedAvailable, id)}
          onToggleAll={rows => toggleAll(setCheckedAvailable, rows)}
          empty={loading ? 'Loading inventory...' : 'No available inventory.'}
          prefix="Available"
        />
        <footer className="cn-goods-foot">
          <span>{formatSelectionCount(checkedAvailable.size)} selected</span>
          <span>{formatSelectionCount(visibleAvailable.length)} available</span>
        </footer>
      </article>

      <div className="cn-transfer">
        <button type="button" aria-label="Move selected inventory to Selected Goods" disabled={!checkedAvailable.size || Boolean(busy)} onClick={moveRight}><ArrowRight size={14} strokeWidth={2.2} /></button>
        <button type="button" aria-label="Move selected goods to Available Inventory" disabled={!checkedSelected.size || Boolean(busy)} onClick={moveLeft}><ArrowLeft size={14} strokeWidth={2.2} /></button>
      </div>

      <article className="cn-card cn-goods-panel">
        <header className="cn-goods-panel-head">
          <h2><span className="cn-goods-title-icon"><Package size={14} strokeWidth={1.7} /></span>Selected Goods</h2>
          <div>
            <button className="cn-text-btn" type="button" onClick={() => toggleAll(setCheckedSelected, visibleSelected)}>Select All</button>
            <button className="cn-text-btn" type="button" onClick={() => setCheckedSelected(new Set())}>Deselect</button>
          </div>
        </header>
        <div className="cn-goods-tools">
          <label className="wh-search">
            <Search size={13} />
            <input type="search" placeholder="Search selected goods..." value={selectedQuery} onChange={event => setSelectedQuery(event.target.value)} />
          </label>
        </div>
        <GoodsTable
          rows={visibleSelected}
          checked={checkedSelected}
          onToggle={id => toggleSet(setCheckedSelected, id)}
          onToggleAll={rows => toggleAll(setCheckedSelected, rows)}
          empty={loading ? 'Loading selected goods...' : 'No selected goods.'}
          prefix="Selected"
        />
        <footer className="cn-goods-foot">
          <span>Total: {formatSelectionCount(selected.length)} • {formatCount(summary.selected_weight)} kg</span>
          <span>{formatSelectionCount(checkedSelected.size)} selected</span>
        </footer>
      </article>
    </div>

    <div className="cn-goods-bar">
      {mode === 'edit' && <button className="inv-export cn-save-draft" type="button" disabled={Boolean(busy)} onClick={handleCancel}>{busy === 'cancel' ? 'Cancelling...' : 'Cancel'}</button>}
      <button className="inv-export cn-save-draft" type="button" onClick={() => handleSave()} disabled={Boolean(busy)}>
        <Save size={14} strokeWidth={1.8} />
        {busy === 'save' ? 'Saving...' : 'Save Draft'}
      </button>
      <p className="cn-goods-summary">
        Selected: {formatSelectionCount(selected.length)} • {formatCount(summary.selected_weight)} kg
        <span>Remaining: {formatCount(summary.remaining_payload)} kg ({formatDecimal(summary.remaining_volume)} m³)</span>
      </p>
      <button className="wh-add cn-continue" type="button" onClick={handleContinue} disabled={Boolean(busy)}>
        Save & Continue to Cartons
      </button>
    </div>
  </section>;
}
